// Per-leg required-field validation for NewTripModal. Bug 3 audit ops manager
// 2026-05-25: antes hacía return-on-first-error y el usuario corregía/submit/
// corregía/submit en ciclos (4-5 ciclos para crear un internacional). Ahora
// acumula TODOS los errores en una sola pasada y los devuelve juntos.
//
// Retorna:
//   - `null` cuando todo pasa.
//   - `{ error, legIndex, errors }` cuando falla. `error` y `legIndex` son
//     los del PRIMER error (back-compat con callers que solo leen esos);
//     `errors` es el array completo de `{ error, legIndex }` para mostrar
//     la lista entera en el banner.
import { composeName } from "@/lib/newTripConstants";

export function validateLegs(legs, userRole, t) {
  const errors = [];
  const push = (i, key, params = {}) =>
    errors.push({ error: t(`newTrip.errors.${key}`, { n: i + 1, ...params }), legIndex: i });

  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i];

    // Origin
    if (!leg.origin || leg.origin.trim().length < 2) {
      push(i, "originRequired");
    } else if (userRole !== "owner" && leg.origin.length !== 4) {
      push(i, "originIcaoInvalid");
    }

    // Destination
    if (!leg.destination || leg.destination.trim().length < 2) {
      push(i, "destinationRequired");
    } else if (userRole !== "owner" && leg.destination.length !== 4) {
      push(i, "destinationIcaoInvalid");
    }

    // Origin and destination cannot be the same (only if both present + non-trivial)
    if (
      leg.origin &&
      leg.destination &&
      leg.origin.trim().length >= 2 &&
      leg.destination.trim().length >= 2 &&
      leg.origin.toUpperCase() === leg.destination.toUpperCase()
    ) {
      push(i, "originDestSame");
    }

    // Departure date ALWAYS required
    if (!leg.departure_date) push(i, "departureDateRequired");

    // Departure time required UNLESS time_tbd
    if (!leg.time_tbd && !leg.departure_time) {
      push(i, "departureTimeRequired");
    } else if (!leg.time_tbd && leg.departure_time) {
      const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
      if (!timeRegex.test(leg.departure_time)) push(i, "timeFormatInvalid");
    }

    // Crew (skipped for owners)
    if (userRole !== "owner") {
      const totalCrew =
        (leg.crew || []).length + (leg.external_crew || []).filter((e) => e.name?.trim()).length;
      if (totalCrew === 0) push(i, "crewRequired");
    }

    // External crew names
    for (let c = 0; c < (leg.external_crew || []).length; c++) {
      if (!leg.external_crew[c].name || !leg.external_crew[c].name.trim()) {
        push(i, "externalCrewNameRequired", { c: c + 1 });
      }
    }

    // Passenger validation (non-ferry)
    if (!leg.is_ferry_flight) {
      const passengerCount = leg.passenger_count || 0;
      if (passengerCount > 0) {
        for (let p = 0; p < leg.passengers.length; p++) {
          const px = leg.passengers[p];
          if (!px.first_name?.trim() || !px.last_name?.trim()) {
            push(i, "passengerNameRequired", { p: p + 1 });
          }
        }

        // International + passport requirements (skipped for owner — surfaces
        // as BLOCKED in OCC via FRE instead of hard-blocking at submit).
        // Bug consolidación 2026-06-02: antes 4 alertas por pasajero (una por
        // campo) inundaban el banner (3 piernas × 1 pax = 12 líneas). Ahora se
        // agrupan los campos faltantes en una sola alerta por pasajero/pierna.
        if (leg.is_international && userRole !== "owner") {
          for (let p = 0; p < leg.passengers.length; p++) {
            const px = leg.passengers[p];
            const missing = [];
            if (!px.passport_number || !px.passport_number.trim()) {
              missing.push(t("newTrip.errors.passportFields.number"));
            }
            if (!px.passport_expiration)
              missing.push(t("newTrip.errors.passportFields.expiration"));
            if (!px.passport_country || !px.passport_country.trim()) {
              missing.push(t("newTrip.errors.passportFields.country"));
            }
            if (!px.dob) missing.push(t("newTrip.errors.passportFields.dob"));
            if (missing.length > 0) {
              push(i, "passportIncomplete", { p: p + 1, fields: missing.join(", ") });
            }
          }
        }
      }
    }
  }

  if (errors.length === 0) return null;
  // Back-compat: callers existentes leen `error` y `legIndex` del primero.
  // Nuevo: `errors` con la lista completa.
  return { error: errors[0].error, legIndex: errors[0].legIndex, errors };
}

// Build the per-leg payload sent to /trips or /trips/{id}/legs.
// Identical shape to the original mapping (preserving keys/order even when
// values are empty strings vs null) so the backend contract is unchanged.
export function buildLegPayload(leg, tripStatus, paxFullName) {
  // Combine assigned crew and external crew
  const allCrew = [
    ...(leg.crew || []),
    ...(leg.external_crew || [])
      .map((e) => ({
        ...e,
        // `name` may be stale if only parts were set; recompose as a fallback.
        name: (e.name || "").trim() || composeName(e.first_name, e.middle_name, e.last_name),
      }))
      .filter((e) => e.name)
      .map((e) => ({
        user_id: e.id,
        first_name: e.first_name || "",
        middle_name: e.middle_name || "",
        last_name: e.last_name || "",
        name: e.name,
        phone: e.phone || "",
        email: e.email || "",
        role: "external",
        flight_role: e.flight_role,
        is_external: true,
      })),
  ];

  return {
    origin: leg.origin.toUpperCase(),
    destination: leg.destination.toUpperCase(),
    // Always include date, time only if not TBD
    departure_time:
      leg.departure_date && !leg.time_tbd && leg.departure_time
        ? `${leg.departure_date}T${leg.departure_time}`
        : leg.departure_date || null,
    arrival_time:
      leg.arrival_date && leg.arrival_time ? `${leg.arrival_date}T${leg.arrival_time}` : "",
    status: tripStatus,
    time_tbd: leg.time_tbd,
    is_international: leg.is_international,
    is_ferry_flight: leg.is_ferry_flight,
    passengers: leg.is_ferry_flight ? [] : leg.passengers.filter((p) => paxFullName(p)),
    crew: allCrew,
    catering_notes: leg.catering_notes,
    ground_service_notes: leg.ground_service_notes,
    alternate_airport: leg.alternate_airport || "",
    fuel_order: leg.fuel_order || null,
  };
}
